import React, { useEffect, useMemo, useState } from 'react';
import { Building, FileText, Plus, Trash2, FileDown, Loader2, ArrowRight, CheckCircle2, AlertTriangle, Camera, Upload, ImageIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import api, { clientsAPI } from '../lib/api';
import { toast } from 'sonner';
import ModuleTasksPanel from '../components/ModuleTasksPanel';
import { formatCurrency, formatDate } from '../lib/utils';

const PROJECT_TYPES = ['Villa', 'Villa R+1', 'Villa R+2', 'Immeuble', 'Maison', 'Magasin', 'Bureau', 'Autre'];

const STATUS_BADGE = {
  draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Envoye', cls: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Accepte', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Rejete', cls: 'bg-red-100 text-red-700' },
  converted: { label: 'Converti', cls: 'bg-violet-100 text-violet-700' },
  active: { label: 'Actif', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Termine', cls: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Annule', cls: 'bg-red-100 text-red-700' },
  paid: { label: 'Paye', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
  overdue: { label: 'En retard', cls: 'bg-red-100 text-red-700' },
  partial: { label: 'Partiel', cls: 'bg-orange-100 text-orange-700' },
};

const emptyLine = () => ({ description: '', unit: 'u', quantity: 1, unit_price: 0 });

const Construction = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);
  const [tab, setTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quote form
  const [openQuote, setOpenQuote] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [qForm, setQForm] = useState({
    client_id: '', project_type: 'Villa', project_location: '', project_description: '',
    superficie_m2: '', rooms: '', line_items: [emptyLine()], tva_rate: 0, valid_until: '', notes: '',
  });

  // Convert dialog
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertingQuote, setConvertingQuote] = useState(null);
  const [convertForm, setConvertForm] = useState({
    payment_mode: 'monthly', monthly_count: 12, advance_amount: 0,
    start_date: new Date().toISOString().slice(0, 10), expected_end_date: '', penalty_per_day: '',
  });

  // Contract details
  const [openContract, setOpenContract] = useState(false);
  const [contractDetails, setContractDetails] = useState(null);

  // Payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount_paid: '', payment_method: 'especes', paid_date: new Date().toISOString().slice(0, 10), reference: '', notes: '' });

  // Progress photos
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [photoPhase, setPhotoPhase] = useState('gros_oeuvre');
  const [photoCaption, setPhotoCaption] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [q, c, cl] = await Promise.all([
        api.get('/construction/quotes'),
        api.get('/construction/contracts'),
        clientsAPI.getAll(),
      ]);
      setQuotes(q.data || []);
      setContracts(c.data || []);
      setClients(cl.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const computedTotal = useMemo(() => {
    const subtotal = qForm.line_items.reduce((s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0), 0);
    const tva = subtotal * ((parseFloat(qForm.tva_rate) || 0) / 100);
    return { subtotal, tva, total: subtotal + tva };
  }, [qForm.line_items, qForm.tva_rate]);

  const openQuoteForm = (q = null) => {
    if (q) {
      setEditingQuote(q);
      setQForm({
        client_id: q.client_id, project_type: q.project_type, project_location: q.project_location,
        project_description: q.project_description || '', superficie_m2: q.superficie_m2 || '',
        rooms: q.rooms || '',
        line_items: q.line_items?.length ? q.line_items.map(li => ({ ...li })) : [emptyLine()],
        tva_rate: q.tva_rate || 0, valid_until: q.valid_until || '', notes: q.notes || '',
      });
    } else {
      setEditingQuote(null);
      setQForm({
        client_id: '', project_type: 'Villa', project_location: '', project_description: '',
        superficie_m2: '', rooms: '', line_items: [emptyLine()], tva_rate: 0, valid_until: '', notes: '',
      });
    }
    setOpenQuote(true);
  };

  const updateLineItem = (idx, field, val) => {
    setQForm(f => ({
      ...f,
      line_items: f.line_items.map((li, i) => i === idx ? { ...li, [field]: val } : li),
    }));
  };
  const addLine = () => setQForm(f => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  const removeLine = (idx) => setQForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const handleSaveQuote = async () => {
    if (!qForm.client_id) return toast.error('Selectionnez un client');
    if (!qForm.project_location) return toast.error('Indiquez le lieu du projet');
    if (qForm.line_items.length === 0 || !qForm.line_items.some(li => li.description && li.unit_price > 0)) {
      return toast.error('Ajoutez au moins une prestation valide');
    }
    try {
      const payload = {
        ...qForm,
        superficie_m2: qForm.superficie_m2 ? parseFloat(qForm.superficie_m2) : null,
        rooms: qForm.rooms ? parseInt(qForm.rooms) : null,
        tva_rate: parseFloat(qForm.tva_rate) || 0,
        line_items: qForm.line_items.filter(li => li.description).map(li => ({
          ...li, quantity: parseFloat(li.quantity) || 0, unit_price: parseFloat(li.unit_price) || 0,
        })),
      };
      if (editingQuote) {
        await api.put(`/construction/quotes/${editingQuote.id}`, payload);
        toast.success('Devis mis a jour');
      } else {
        await api.post('/construction/quotes', payload);
        toast.success('Devis cree');
      }
      setOpenQuote(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteQuote = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try { await api.delete(`/construction/quotes/${id}`); loadAll(); toast.success('Supprime'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const downloadPdf = async (kind, id, ref) => {
    try {
      const r = await api.get(`/construction/${kind}s/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${kind}_${ref}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur de telechargement');
    }
  };

  const openConvert = (q) => {
    setConvertingQuote(q);
    setConvertForm({
      payment_mode: 'monthly', monthly_count: 12, advance_amount: 0,
      start_date: new Date().toISOString().slice(0, 10), expected_end_date: '', penalty_per_day: '',
    });
    setConvertOpen(true);
  };

  const handleConvert = async () => {
    if (!convertingQuote) return;
    try {
      await api.post('/construction/contracts/from-quote', {
        quote_id: convertingQuote.id,
        payment_mode: convertForm.payment_mode,
        monthly_count: parseInt(convertForm.monthly_count) || 1,
        advance_amount: parseFloat(convertForm.advance_amount) || 0,
        start_date: convertForm.start_date,
        expected_end_date: convertForm.expected_end_date || null,
        penalty_per_day: convertForm.penalty_per_day ? parseFloat(convertForm.penalty_per_day) : null,
      });
      toast.success('Contrat cree avec echeancier');
      setConvertOpen(false);
      setTab('contracts');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const showContract = async (c) => {
    try {
      const r = await api.get(`/construction/contracts/${c.id}`);
      setContractDetails(r.data);
      setOpenContract(true);
      // Load progress photos
      setProgressLoading(true);
      try {
        const p = await api.get(`/construction/contracts/${c.id}/progress`);
        setProgressPhotos(p.data || []);
      } catch (_) {
        setProgressPhotos([]);
      } finally {
        setProgressLoading(false);
      }
    } catch (e) {
      toast.error('Erreur de chargement');
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !contractDetails) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('phase', photoPhase);
      fd.append('caption', photoCaption);
      const r = await api.post(`/construction/contracts/${contractDetails.id}/progress`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProgressPhotos((prev) => [r.data, ...prev]);
      setPhotoCaption('');
      toast.success('Photo ajoutee');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await api.delete(`/construction/contracts/${contractDetails.id}/progress/${photoId}`);
      setProgressPhotos((prev) => prev.filter((p) => p.id !== photoId));
      toast.success('Supprimee');
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  const openPay = (payment) => {
    setPayTarget(payment);
    setPayForm({
      amount_paid: payment.amount_due - (payment.amount_paid || 0),
      payment_method: 'especes',
      paid_date: new Date().toISOString().slice(0, 10),
      reference: '', notes: '',
    });
    setPayOpen(true);
  };

  const downloadReceipt = async (contractId, paymentNumber) => {
    try {
      const resp = await api.get(
        `/construction/contracts/${contractId}/payments/${paymentNumber}/receipt`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `recu_paiement_${paymentNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de generation du recu');
    }
  };

  const sendReceiptViaWhatsApp = async (contract, paymentNumber) => {
    if (!contract) return;
    let phone = contract.client_phone || '';
    if (!phone && contract.client_id) {
      try {
        const c = await api.get(`/clients/${contract.client_id}`);
        phone = c.data?.phone || '';
      } catch {}
    }
    if (!phone) {
      toast.error('Numero de telephone du client absent');
      return;
    }
    try {
      const r = await api.post(
        `/construction/contracts/${contract.id}/payments/${paymentNumber}/share`,
      );
      const publicUrl = `${window.location.origin}${r.data.public_url}`;
      const payment = (contract.payment_schedule || []).find(p => p.payment_number === paymentNumber) || {};
      const amount = (payment.amount_paid || 0).toLocaleString();
      const message = `Bonjour ${contract.client_name || ''},\n\n` +
        `Nous accusons reception de votre paiement de ${amount} FCFA pour le contrat ${contract.contract_number} (echeance n${paymentNumber}).\n\n` +
        `Telechargez votre recu : ${publicUrl}\n\n` +
        `(Lien valable 7 jours)\n\n` +
        `Merci pour votre confiance.`;
      const cleanPhone = phone.replace(/[^0-9+]/g, '').replace('+', '');
      const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
      window.open(waUrl, '_blank');
      toast.success('WhatsApp ouvert avec le lien du recu');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur generation lien partage');
    }
  };

  const handleRecordPayment = async () => {
    if (!payTarget || !contractDetails) return;
    try {
      const resp = await api.post(
        `/construction/contracts/${contractDetails.id}/payments/${payTarget.payment_number}/record`,
        {
          amount_paid: parseFloat(payForm.amount_paid) || 0,
          payment_method: payForm.payment_method,
          paid_date: payForm.paid_date,
          reference: payForm.reference || null,
          notes: payForm.notes || null,
        },
      );
      const surplusInfo = resp.data?.surplus_carried || 0;
      if (surplusInfo > 0) {
        toast.success(`Paiement enregistre (surplus de ${surplusInfo.toLocaleString()} FCFA reporte)`);
      } else {
        toast.success('Paiement enregistre');
      }
      setPayOpen(false);
      const r = await api.get(`/construction/contracts/${contractDetails.id}`);
      setContractDetails(r.data);
      loadAll();
      // Auto-download the receipt PDF
      setTimeout(() => downloadReceipt(contractDetails.id, payTarget.payment_number), 400);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const StatusBadge = ({ status }) => {
    const s = STATUS_BADGE[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
    return <Badge className={`${s.cls} text-xs`}>{s.label}</Badge>;
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Accès réservé aux administrateurs</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="construction-page">
      <ModuleTasksPanel moduleKey="construction" moduleLabel="Construction" />
      <div>
        <h1 className="text-2xl font-semibold text-[#1B3A4B] flex items-center gap-2">
          <Building className="w-6 h-6 text-[#D4A745]" />
          Contrats de Construction
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Gerez vos devis, contrats et paiements mensuels des projets de construction.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="quotes" data-testid="tab-quotes">Devis ({quotes.length})</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="tab-contracts">Contrats ({contracts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="quotes" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openQuoteForm()} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="new-quote-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau devis
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {loading ? <div className="h-32 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#D4A745]" /></div>
              : quotes.length === 0 ? <p className="text-center text-gray-500 py-8">Aucun devis enregistre.</p>
              : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Projet</TableHead>
                    <TableHead className="text-right">Total TTC</TableHead><TableHead>Statut</TableHead><TableHead className="w-40"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {quotes.map(q => (
                      <TableRow key={q.id}>
                        <TableCell className="font-mono text-xs">{q.quote_number}</TableCell>
                        <TableCell>{q.client_name || '-'}</TableCell>
                        <TableCell><div className="text-sm font-medium">{q.project_type}</div><div className="text-xs text-gray-500">{q.project_location}</div></TableCell>
                        <TableCell className="text-right font-mono font-semibold">{formatCurrency(q.total, 'XOF')}</TableCell>
                        <TableCell><StatusBadge status={q.status} /></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => downloadPdf('quote', q.id, q.quote_number)} title="PDF" className="text-blue-600"><FileDown className="w-4 h-4" /></Button>
                            {q.status !== 'converted' && (
                              <Button size="sm" variant="ghost" onClick={() => openConvert(q)} title="Convertir en contrat" className="text-emerald-600"><ArrowRight className="w-4 h-4" /></Button>
                            )}
                            {q.status !== 'converted' && (
                              <Button size="sm" variant="ghost" onClick={() => openQuoteForm(q)} title="Modifier"><FileText className="w-4 h-4" /></Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteQuote(q.id)} className="text-red-500" title="Supprimer"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contracts" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              {contracts.length === 0 ? <p className="text-center text-gray-500 py-8">Aucun contrat. Convertissez un devis pour creer un contrat.</p>
              : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Projet</TableHead>
                    <TableHead>Demarrage</TableHead><TableHead className="text-right">Montant</TableHead>
                    <TableHead>Statut</TableHead><TableHead className="w-32"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {contracts.map(c => (
                      <TableRow key={c.id} className="cursor-pointer hover:bg-gray-50" onClick={() => showContract(c)} data-testid={`contract-row-${c.id}`}>
                        <TableCell className="font-mono text-xs">{c.contract_number}</TableCell>
                        <TableCell>{c.client_name || '-'}</TableCell>
                        <TableCell><div className="text-sm font-medium">{c.project_type}</div><div className="text-xs text-gray-500">{c.project_location}</div></TableCell>
                        <TableCell>{c.start_date}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{formatCurrency(c.total, 'XOF')}</TableCell>
                        <TableCell><StatusBadge status={c.status} /></TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => downloadPdf('contract', c.id, c.contract_number)} className="text-blue-600"><FileDown className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quote dialog */}
      <Dialog open={openQuote} onOpenChange={setOpenQuote}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuote ? `Modifier devis ${editingQuote.quote_number}` : 'Nouveau devis'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client *</Label>
                <Select value={qForm.client_id} onValueChange={(v) => setQForm(f => ({ ...f, client_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Selectionner" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type de projet *</Label>
                <Select value={qForm.project_type} onValueChange={(v) => setQForm(f => ({ ...f, project_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Lieu du projet *</Label>
                <Input value={qForm.project_location} onChange={(e) => setQForm(f => ({ ...f, project_location: e.target.value }))} placeholder="Ex: Bamako, Bacodjicoroni" />
              </div>
              <div>
                <Label>Superficie (m²)</Label>
                <Input type="number" value={qForm.superficie_m2} onChange={(e) => setQForm(f => ({ ...f, superficie_m2: e.target.value }))} />
              </div>
              <div>
                <Label>Nombre de pieces</Label>
                <Input type="number" value={qForm.rooms} onChange={(e) => setQForm(f => ({ ...f, rooms: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea rows={2} value={qForm.project_description} onChange={(e) => setQForm(f => ({ ...f, project_description: e.target.value }))} />
              </div>
            </div>

            {/* Line items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base">Prestations</Label>
                <Button size="sm" variant="outline" onClick={addLine}><Plus className="w-3 h-3 mr-1" /> Ligne</Button>
              </div>
              {qForm.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    <Input value={li.description} onChange={(e) => updateLineItem(i, 'description', e.target.value)} placeholder="Description" data-testid={`line-desc-${i}`} />
                  </div>
                  <div className="col-span-2"><Input value={li.unit} onChange={(e) => updateLineItem(i, 'unit', e.target.value)} placeholder="u/m²" /></div>
                  <div className="col-span-1"><Input type="number" value={li.quantity} onChange={(e) => updateLineItem(i, 'quantity', e.target.value)} /></div>
                  <div className="col-span-3"><Input type="number" value={li.unit_price} onChange={(e) => updateLineItem(i, 'unit_price', e.target.value)} placeholder="P.U." /></div>
                  <div className="col-span-1">
                    <Button size="sm" variant="ghost" onClick={() => removeLine(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="bg-gray-50 p-3 rounded grid grid-cols-3 gap-3 text-sm">
              <div><span className="text-gray-500">Sous-total HT:</span> <span className="font-semibold">{formatCurrency(computedTotal.subtotal, 'XOF')}</span></div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">TVA (%):</span>
                <Input type="number" value={qForm.tva_rate} onChange={(e) => setQForm(f => ({ ...f, tva_rate: e.target.value }))} className="w-20 h-8" />
                <span>{formatCurrency(computedTotal.tva, 'XOF')}</span>
              </div>
              <div className="text-right text-base font-bold text-[#B48A36]">
                TOTAL: {formatCurrency(computedTotal.total, 'XOF')}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valable jusqu'au</Label><Input type="date" value={qForm.valid_until} onChange={(e) => setQForm(f => ({ ...f, valid_until: e.target.value }))} /></div>
              <div><Label>Notes</Label><Input value={qForm.notes} onChange={(e) => setQForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenQuote(false)}>Annuler</Button>
            <Button onClick={handleSaveQuote} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="save-quote-btn">
              {editingQuote ? 'Enregistrer' : 'Creer le devis'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert quote → contract */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convertir en contrat</DialogTitle>
            <DialogDescription>Devis {convertingQuote?.quote_number} - {formatCurrency(convertingQuote?.total || 0, 'XOF')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Mode de paiement</Label>
              <Select value={convertForm.payment_mode} onValueChange={(v) => setConvertForm(f => ({ ...f, payment_mode: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensualites</SelectItem>
                  <SelectItem value="milestone">Tranches (jalons)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {convertForm.payment_mode === 'monthly' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre de mensualites</Label><Input type="number" value={convertForm.monthly_count} onChange={(e) => setConvertForm(f => ({ ...f, monthly_count: e.target.value }))} /></div>
                <div><Label>Acompte initial (FCFA)</Label><Input type="number" value={convertForm.advance_amount} onChange={(e) => setConvertForm(f => ({ ...f, advance_amount: e.target.value }))} /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date de debut *</Label><Input type="date" value={convertForm.start_date} onChange={(e) => setConvertForm(f => ({ ...f, start_date: e.target.value }))} /></div>
              <div><Label>Livraison prevue</Label><Input type="date" value={convertForm.expected_end_date} onChange={(e) => setConvertForm(f => ({ ...f, expected_end_date: e.target.value }))} /></div>
            </div>
            <div><Label>Penalite par jour de retard (FCFA)</Label><Input type="number" value={convertForm.penalty_per_day} onChange={(e) => setConvertForm(f => ({ ...f, penalty_per_day: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)}>Annuler</Button>
            <Button onClick={handleConvert} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-convert-btn"><CheckCircle2 className="w-4 h-4 mr-2" /> Convertir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract details */}
      <Dialog open={openContract} onOpenChange={setOpenContract}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contrat {contractDetails?.contract_number}</DialogTitle>
            <DialogDescription>{contractDetails?.client_name} - {contractDetails?.project_type} a {contractDetails?.project_location}</DialogDescription>
          </DialogHeader>
          {contractDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Total contrat</p>
                  <p className="text-lg font-bold text-[#1B3A4B]">{formatCurrency(contractDetails.total, 'XOF')}</p>
                </div>
                <div className="bg-emerald-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Encaisse</p>
                  <p className="text-lg font-bold text-emerald-700">
                    {formatCurrency((contractDetails.payment_schedule || []).reduce((s, p) => s + (p.amount_paid || 0), 0), 'XOF')}
                  </p>
                </div>
                <div className="bg-orange-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Reste a payer</p>
                  <p className="text-lg font-bold text-orange-700">
                    {formatCurrency(contractDetails.total - (contractDetails.payment_schedule || []).reduce((s, p) => s + (p.amount_paid || 0), 0), 'XOF')}
                  </p>
                </div>
              </div>

              <Tabs defaultValue="schedule" className="w-full">
                <TabsList className="grid w-full grid-cols-2 max-w-md">
                  <TabsTrigger value="schedule" data-testid="contract-tab-schedule">Echeancier</TabsTrigger>
                  <TabsTrigger value="photos" data-testid="contract-tab-photos">
                    <Camera className="w-4 h-4 mr-1" /> Photos chantier ({progressPhotos.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="schedule" className="space-y-3">
                <h3 className="font-semibold mb-2">Echeancier de paiement</h3>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>N°</TableHead><TableHead>Libelle</TableHead><TableHead>Echeance</TableHead>
                    <TableHead className="text-right">Du</TableHead><TableHead className="text-right">Paye</TableHead>
                    <TableHead>Statut</TableHead><TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(contractDetails.payment_schedule || []).map((p) => {
                      const reducedBy = p.covered_by_surplus || 0;
                      const original = p.original_amount_due;
                      const surplusOnLine = p.surplus_carried || 0;
                      return (
                      <TableRow key={p.payment_number} data-testid={`payment-row-${p.payment_number}`}>
                        <TableCell>{p.payment_number}</TableCell>
                        <TableCell>
                          {p.label}
                          {reducedBy > 0 && (
                            <div className="mt-1">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                                title={`Reduit grace au surplus du paiement #${p.covered_by_payment_number || ''}`}
                                data-testid={`surplus-badge-${p.payment_number}`}
                              >
                                Reduit de {formatCurrency(reducedBy, 'XOF')}
                              </span>
                            </div>
                          )}
                          {surplusOnLine > 0 && (
                            <div className="mt-1">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200"
                                title="Surplus reporte sur les echeances suivantes"
                              >
                                Surplus +{formatCurrency(surplusOnLine, 'XOF')} reporte
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{p.due_date}</TableCell>
                        <TableCell className="text-right font-mono">
                          {original && original !== p.amount_due ? (
                            <div>
                              <span className="line-through text-gray-400 text-xs mr-1">{formatCurrency(original, 'XOF')}</span>
                              <span>{formatCurrency(p.amount_due, 'XOF')}</span>
                            </div>
                          ) : (
                            formatCurrency(p.amount_due, 'XOF')
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(p.amount_paid || 0, 'XOF')}</TableCell>
                        <TableCell><StatusBadge status={p.status} /></TableCell>
                        <TableCell>
                          {p.status !== 'paid' && (
                            <Button size="sm" onClick={() => openPay(p)} className="bg-emerald-600 hover:bg-emerald-700 h-7" data-testid={`pay-btn-${p.payment_number}`}>
                              Encaisser
                            </Button>
                          )}
                          {p.status === 'paid' && p.paid_date && p.payment_method !== 'report_surplus' && (
                            <div className="flex flex-wrap gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadReceipt(contractDetails.id, p.payment_number)}
                                className="h-7"
                                data-testid={`receipt-btn-${p.payment_number}`}
                              >
                                Reçu PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendReceiptViaWhatsApp(contractDetails, p.payment_number)}
                                className="h-7 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                data-testid={`whatsapp-receipt-btn-${p.payment_number}`}
                                title="Envoyer le reçu au client par WhatsApp"
                              >
                                WhatsApp
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </TabsContent>

                <TabsContent value="photos" className="space-y-4">
                  {/* Upload bar */}
                  <div className="bg-gray-50 border rounded-lg p-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Phase</Label>
                        <Select value={photoPhase} onValueChange={setPhotoPhase}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fondation">Fondation</SelectItem>
                            <SelectItem value="gros_oeuvre">Gros oeuvre</SelectItem>
                            <SelectItem value="toiture">Toiture</SelectItem>
                            <SelectItem value="second_oeuvre">Second oeuvre</SelectItem>
                            <SelectItem value="finition">Finition</SelectItem>
                            <SelectItem value="livraison">Livraison</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Legende (optionnel)</Label>
                        <Input value={photoCaption} onChange={(e) => setPhotoCaption(e.target.value)} placeholder="Ex: Coulage dalle 1er etage" />
                      </div>
                    </div>
                    <Label className="cursor-pointer flex items-center justify-center gap-2 bg-[#1B3A4B] hover:bg-[#142D3A] text-white py-3 rounded-lg text-sm font-medium" data-testid="upload-photo-label">
                      {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploadingPhoto ? 'Upload en cours...' : 'Choisir une photo'}
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUploadPhoto} disabled={uploadingPhoto} data-testid="upload-photo-input" />
                    </Label>
                  </div>

                  {/* Photos grid */}
                  {progressLoading ? (
                    <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#D4A745]" /></div>
                  ) : progressPhotos.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Aucune photo de chantier pour ce contrat.</p>
                      <p className="text-xs mt-1">Documentez l'avancement des travaux phase par phase.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {progressPhotos.map((p) => (
                        <div key={p.id} className="relative group rounded-lg overflow-hidden border-2 border-gray-200 bg-white" data-testid={`progress-photo-${p.id}`}>
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`}
                            alt={p.caption || p.phase}
                            className="w-full aspect-square object-cover"
                          />
                          <div className="absolute top-1 left-1">
                            <Badge className="bg-black/70 text-white text-xs capitalize">
                              {p.phase.replace('_', ' ')}
                            </Badge>
                          </div>
                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition">
                            <Button size="sm" variant="destructive" className="h-7 w-7 p-0" onClick={() => handleDeletePhoto(p.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <div className="p-2 text-xs">
                            {p.caption && <p className="font-medium truncate">{p.caption}</p>}
                            <p className="text-gray-500">{formatDate(p.uploaded_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record payment */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Encaisser - {payTarget?.label}</DialogTitle>
            <DialogDescription>Montant du : {formatCurrency(payTarget?.amount_due || 0, 'XOF')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Montant recu *</Label><Input type="number" value={payForm.amount_paid} onChange={(e) => setPayForm(f => ({ ...f, amount_paid: e.target.value }))} data-testid="pay-amount-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Methode</Label>
                <Select value={payForm.payment_method} onValueChange={(v) => setPayForm(f => ({ ...f, payment_method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="especes">Especes</SelectItem>
                    <SelectItem value="virement">Virement</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={payForm.paid_date} onChange={(e) => setPayForm(f => ({ ...f, paid_date: e.target.value }))} /></div>
            </div>
            <div><Label>Reference (optionnel)</Label><Input value={payForm.reference} onChange={(e) => setPayForm(f => ({ ...f, reference: e.target.value }))} /></div>
            <div><Label>Notes</Label><Input value={payForm.notes} onChange={(e) => setPayForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Annuler</Button>
            <Button onClick={handleRecordPayment} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-pay-btn"><CheckCircle2 className="w-4 h-4 mr-2" /> Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Construction;
